import React, { FC, useState, useCallback } from 'react';

import Table from 'molecules/Table';
import PaginationActions from 'molecules/Table/TablePagination/Actions';

import { StyledTableContainer } from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';

import { snakeCase } from 'lodash-es';
import { palette } from 'utils/styles/variables';
import { contractTypeMap } from 'utils/constants/common';

import { ReactComponent as InfoIcon } from 'assets/icons/info-filled.svg';

import { Order } from 'organisms/InvoicesTable/types';
import { IZohoProject } from 'pages/CompanyPage/types';
import { ProjectErrorModal } from 'molecules/ProjectErrorTable/ProjectErrorModal';
import { reds } from 'utils/styles/color';

export const initialValue = { page: 1, limit: 20 };

const ZohoProjectsTable: FC<{
  loading?: boolean;
  data: { projects: IZohoProject[]; count: number };
  onChange: (f: typeof initialValue, s?: string) => void;
}> = ({ loading, data: { projects = [], count = 0 }, onChange }) => {
  const [pagination, setPagination] = useState(initialValue);
  const [project, setProject] = useState<IZohoProject | null>(null);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('name');

  const handleRequestSort = useCallback(
    async (event: React.MouseEvent<unknown>, property: 'name') => {
      const isAsc = orderBy === property && order === 'asc';
      const nextState = { ...pagination, page: 1 };

      onChange(
        nextState,
        `sort[${snakeCase(property)}]=${isAsc ? 'desc' : 'asc'}`
      );

      setPagination(nextState);
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [pagination, orderBy, order, onChange]
  );

  const handleChangePage = useCallback(
    async (event: unknown, newPage: number) => {
      const nextState = { ...pagination, page: newPage + 1 };
      const sort =
        order && orderBy ? `sort[${snakeCase(orderBy)}]=${order}` : '';

      onChange(nextState, sort);
      setPagination(nextState);
    },
    [pagination, orderBy, order, onChange]
  );

  const handleChangeRowsPerPage = useCallback(
    async (option: number) => {
      const nextState = { limit: option, page: 1 };
      const sort =
        order && orderBy ? `sort[${snakeCase(orderBy)}]=${order}` : '';

      onChange(nextState, sort);
      setPagination(nextState);
    },
    [orderBy, order, pagination, onChange]
  );

  const handleErrorsShow = (prj: IZohoProject) => {
    document.body.setAttribute('id', 'body');
    setProject(prj);
  };

  const columns = [
    {
      key: 'name',
      label: 'Project name',
      sortable: true,
      width: '30%',
      render: ({ name }: IZohoProject) => (
        <StyledCellText>{name}</StyledCellText>
      ),
    },
    {
      key: 'group_name',
      label: 'Group',
      sortable: true,
      width: '24%',
      render: ({ group_name }: IZohoProject) => (
        <StyledCellText>{group_name}</StyledCellText>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: '10%',
      render: ({ status }: IZohoProject) => (
        <StyledCellText>{status}</StyledCellText>
      ),
    },
    {
      key: 'integrated_status',
      label: 'Integrated status',
      sortable: true,
      width: '10%',
      render: ({ integrated_with_system }: IZohoProject) => (
        <StyledCellText>{integrated_with_system}</StyledCellText>
      ),
    },
    {
      key: 'contract_type',
      label: 'Contract type',
      sortable: true,
      width: '14%',
      render: ({ contract_type }: IZohoProject) => (
        <StyledCellText>{contractTypeMap[contract_type]}</StyledCellText>
      ),
    },
    {
      key: 'automatic_invoice_creation',
      label: 'Auto-invoicing',
      sortable: true,
      width: '10%',
      render: ({ automatic_invoice_creation }: IZohoProject) => (
        <StyledCellText>{automatic_invoice_creation}</StyledCellText>
      ),
    },
    {
      key: 'billing_data_check',
      label: 'Billing data check',
      sortable: false,
      width: '10%',
      render: (prj: IZohoProject) => {
        if (prj.error_labels_links && prj.error_labels_links.length) {
          const isError = prj.error_labels_links.some(
            ({ error_states }) => error_states && error_states.length
          );

          if (isError) {
            return (
              // eslint-disable-next-line
              // @ts-ignore
              <StyledCellText
                sx={{ cursor: 'pointer' }}
                onClick={() => handleErrorsShow(prj)}
              >
                <InfoIcon color={reds[300]} />
              </StyledCellText>
            );
          }
        }

        return (
          <StyledCellText
            sx={{ cursor: 'pointer' }}
            onClick={() => handleErrorsShow(prj)}
          >
            <InfoIcon color={palette.success[300]} />
          </StyledCellText>
        );
      },
    },
    // {
    //   key: 'actions',
    //   label: '',
    //   sortable: false,
    //   width: '10%',
    //   render: (project: CloudOpsProjectsType) => (
    //     <StyledCellContainer $right $hStack>
    //       <SettingsIcon
    //         onClick={() => onSettings(project)}
    //         style={{
    //           color: '#6F9FE9',
    //           position: 'relative',
    //           cursor: 'pointer',
    //         }}
    //       />
    //     </StyledCellContainer>
    //   ),
    // },
  ];

  return (
    <StyledTableContainer>
      <Table
        data={projects}
        columns={columns}
        idKey="id"
        sx={{ minWidth: 650 }}
        aria-label="simple table"
        loading={loading}
        loadingText="Preparing projects overview. This could take up to a 30 seconds."
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
      />
      <StyledTablePagination
        labelDisplayedRows={() => null}
        rowsPerPageOptions={[]}
        count={count}
        rowsPerPage={pagination.limit}
        page={count ? pagination.page - 1 : 0}
        onPageChange={handleChangePage}
        ActionsComponent={(props) => (
          <PaginationActions
            {...props}
            labelRowsPerPage="Items per page"
            rowsPerPageOptions={[20, 50, 100]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      />

      {project && (
        <ProjectErrorModal project={project} onClose={() => setProject(null)} />
      )}
    </StyledTableContainer>
  );
};

export default ZohoProjectsTable;
